// PWA Install Prompt — listens for beforeinstallprompt, stores the deferred
// event, and renders a discreet banner after the user has opened the app at
// least 2 times. Dismissal is persisted for 30 days.
//
// Standalone detection: hides the banner if the app is already running as
// installed PWA (display-mode: standalone). iOS has no programmatic install,
// so we show manual instructions there.
import React, { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { t } from "@/utils/i18n";

const SESSION_COUNT_KEY = "ac_session_count";
const DISMISS_UNTIL_KEY = "ac_install_dismissed_until";
const MIN_SESSIONS = 2;
const DISMISS_DAYS = 30;

function isStandalone() {
  try {
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

function isIOS() {
  try {
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  } catch {
    return false;
  }
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_UNTIL_KEY);
    if (!raw) return false;
    return Date.now() < parseInt(raw, 10);
  } catch {
    return false;
  }
}

function persistDismiss() {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_UNTIL_KEY, String(until));
  } catch { /* noop */ }
}

function incrementSessionCount() {
  try {
    const cur = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || "0", 10);
    const next = cur + 1;
    localStorage.setItem(SESSION_COUNT_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

export default function PWAInstallPrompt({ lang = "en" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const isIt = (lang || "en").startsWith("it");

  useEffect(() => {
    if (isStandalone()) return; // already installed

    // Count this session
    const count = incrementSessionCount();

    if (isDismissed() || count < MIN_SESSIONS) return;

    // Android / Chrome / Edge: listen for native install prompt
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS has no programmatic install — show manual banner after sessions
    if (isIOS()) {
      setShow(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch { /* noop */ }
      setDeferredPrompt(null);
      setShow(false);
      return;
    }
    if (isIOS()) {
      setShowIOSHelp(true);
      return;
    }
    setShow(false);
  };

  const handleDismiss = () => {
    persistDismiss();
    setShow(false);
  };

  if (!show) return null;

  return (
    <>
      {!showIOSHelp && (
        <div
          className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[360px] z-[150] bg-[#1a1a1d] border-2 border-[#cfb53b]/50 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.7)] p-3.5 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500"
          data-testid="pwa-install-prompt"
          role="dialog"
          aria-live="polite"
        >
          <div className="shrink-0 w-10 h-10 rounded-lg bg-[#cfb53b]/15 border border-[#cfb53b]/40 flex items-center justify-center">
            <Download size={18} className="text-[#cfb53b]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm text-[#cfb53b] leading-tight">
              {t(lang, "pwa_install_title")}
            </p>
            <p className="text-[11px] text-[#cbd5e1]/90 leading-snug mt-0.5">
              {t(lang, "pwa_install_desc")}
            </p>
            <div className="flex items-center gap-2 mt-2.5">
              <button
                onClick={handleInstall}
                className="bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-xs px-3 py-1.5 rounded-md transition-colors"
                data-testid="pwa-install-cta"
              >
                {isIt ? "Installa" : "Install"}
              </button>
              <button
                onClick={handleDismiss}
                className="text-xs text-[#94a3b8] hover:text-[#cfb53b] transition-colors px-2 py-1.5"
                data-testid="pwa-install-dismiss"
              >
                {isIt ? "Più tardi" : "Later"}
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-[#94a3b8] hover:text-[#cfb53b] shrink-0 -mr-1 -mt-1"
            aria-label="Dismiss"
            data-testid="pwa-install-close"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* iOS manual instructions modal */}
      {showIOSHelp && (
        <div
          className="fixed inset-0 z-[200] bg-black/75 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIOSHelp(false)}
          data-testid="pwa-ios-help"
        >
          <div
            className="bg-[#1a1a1d] border-2 border-[#cfb53b]/50 rounded-xl p-5 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading text-base text-[#cfb53b] mb-3">
              {isIt ? "Installa su iPhone / iPad" : "Install on iPhone / iPad"}
            </h3>
            <ol className="text-xs text-[#cbd5e1] space-y-2 leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-[#cfb53b] font-heading">1.</span>
                <span>
                  {isIt ? "Tocca" : "Tap"} <Share size={12} className="inline mx-0.5" />{" "}
                  {isIt ? "nella barra di Safari" : "in the Safari bar"}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#cfb53b] font-heading">2.</span>
                <span>{isIt ? 'Scegli "Aggiungi alla schermata Home"' : 'Choose "Add to Home Screen"'}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#cfb53b] font-heading">3.</span>
                <span>{isIt ? 'Tocca "Aggiungi"' : 'Tap "Add"'}</span>
              </li>
            </ol>
            <button
              onClick={() => { setShowIOSHelp(false); handleDismiss(); }}
              className="mt-4 w-full bg-[#cfb53b] hover:bg-[#d4af37] text-[#0a0a0c] font-heading text-sm py-2 rounded-md transition-colors"
              data-testid="pwa-ios-help-close"
            >
              {isIt ? "Ho capito" : "Got it"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
